import React, { useEffect, useMemo, useState } from "react";
import { Container, UncontrolledTooltip, Form, FormGroup, Label, Input, Button, Row, Col, Card, CardBody, FormFeedback, Modal, Spinner, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import TableContainer from '../../../components/Common/TableContainer';
import dayjs from "dayjs";

// Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb";

import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchShifts, createShift, deleteShift } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import Swal from 'sweetalert2';
import CustomTimePicker from "common/CustomTimePicker";
import { Loader } from "lucide-react";
import moment from "moment";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";

const Shift = () => {
    const [accessRight, setAccessRight] = useState(null);
    const location = useLocation();
    const history = useHistory();
    useEffect(() => {
        const rights = getAccessRights(location.pathname, history);
        setAccessRight(rights?.data);
    }, [location]);
    const { canRead, canCreate, canEdit, canDelete, canApprove } = useButtonAccess(accessRight);
    const [isEdit, setIsEdit] = useState(false);
    const [shiftId, setShiftId] = useState(null);
    const [authDetails, setAuthDetails] = useState(null);


    const dispatch = useDispatch();

    const { shifts, loading, error, success } = useSelector((state) => state.shiftReducer || {});

    // Function to compare times
    const timeIsAfter = (startTime, endTime) => {
        const start = new Date(`1970-01-01T${startTime}:00Z`);
        const end = new Date(`1970-01-01T${endTime}:00Z`);
        return end > start;
    };

    const [modal, setModal] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const toggle = () => setModal(!modal);

    const confirmDelete = (shiftId) => {
        const deletePayload = {
            shiftid: shiftId.shiftid,
            unitid: authDetails.unit_id,
            createdby: authDetails.user_id,
        };

        setDeleteData(deletePayload); // Store in state for modal
        toggle();
    };

    const handleDelete = () => {
        if (deleteData) {
            const formData = new FormData();
            formData.append("shift_id", deleteData.shiftid);
            formData.append("unit_id", deleteData.unitid);
            formData.append("createdby", deleteData.createdby);

            dispatch(deleteShift(formData)); // Dispatch the FormData payload
        }
        toggle();
    };

    const toMinutes = (t) => {
        if (!t) return null;
        const time = dayjs(t, ["hh:mm A", "HH:mm"], true);
        if (!time.isValid()) return null;
        return time.hour() * 60 + time.minute();
    };

    // Configure max shift length here (hours)
    const MAX_SHIFT_HOURS = 12;
    const MAX_SHIFT_MINUTES = MAX_SHIFT_HOURS * 60;

    // Helper: parse time and, if needed, add 1 day to out if it's before in
    function normalizePair(inTimeStr, outTimeStr, format = "hh:mm A") {
    if (!inTimeStr || !outTimeStr) return null;

    const inMoment = moment(inTimeStr, format);
    let outMoment = moment(outTimeStr, format);

    // if out is same or before in, consider next day
    if (!outMoment.isAfter(inMoment)) {
        outMoment = outMoment.clone().add(1, "day");
    }

    return { inMoment, outMoment };
    }



    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            shiftCode: "",
            shiftName: "",
            shiftType: "",
            inTime: "",
            outTime: "",
            breakOut: "",
            breakIn: "",
            withBreak: false,
        },
        validationSchema: Yup.object({
            shiftCode: Yup.string()
                .required("Shift Code is required")
                .max(10, "Shift Code must not exceed 10 characters")
                .matches(
                    /^[a-zA-Z0-9 ]+$/,
                    "Shift Code allows only alphabets, numbers and spaces"
                ),

            shiftName: Yup.string()
                .required("Shift Name is required")
                .max(50, "Shift Name must not exceed 50 characters")
                .matches(
                    /^[a-zA-Z0-9 ]+$/,
                    "Shift Name allows only alphabets, numbers and spaces"
                ),
            shiftType: Yup.string().required("Shift Type is required"),

            inTime: Yup.string().nullable().required("In Time is required"),

            outTime: Yup.string()
                .nullable()
                .required("Out Time is required")
                .test(
                "is-after-inTime-and-within-max",
                `Out Time must be after In Time and shift duration must be ≤ ${MAX_SHIFT_HOURS} hours`,
                function (value) {
                    const { inTime } = this.parent;
                    if (!inTime || !value) return true;

                    const pair = normalizePair(inTime, value, "hh:mm A");
                    if (!pair) return true;

                    // const { inMoment, outMoment } = pair;
                    // const diffMinutes = outMoment.diff(inMoment, "minutes");
                     const diffMinutes = pair.outMoment.diff(pair.inMoment, "minutes");

                    // must be >0 and <= max
                    return diffMinutes > 0 && diffMinutes <= MAX_SHIFT_MINUTES;
                }
                ),

            withBreak: Yup.boolean(),

            breakOut: Yup.string()
                .nullable()
                .when("withBreak", {
                is: true,
                then: (schema) =>
                    schema
                    .required("Break Out Time is required")
                    .test(
                        "is-between-in-and-out",
                        "Break Out must be between In and Out Time",
                        function (value) {
                        const { inTime, outTime } = this.parent;
                        if (!inTime || !outTime || !value) return true;

                        const shift = normalizePair(inTime, outTime, "hh:mm A");
                        if (!shift) return true;

                        const breakOutMoment = moment(value, "hh:mm A");
                        // if breakOut is before in, assume next day only if that still falls before out (handles overnight breaks)
                        if (!breakOutMoment.isAfter(shift.inMoment)) {
                            breakOutMoment.add(1, "day");
                        }

                        return breakOutMoment.isAfter(shift.inMoment) && breakOutMoment.isBefore(shift.outMoment);
                        }
                    ),
                otherwise: (schema) => schema.nullable().notRequired(),
                }),

            breakIn: Yup.string()
                .nullable()
                .when("withBreak", {
                is: true,
                then: (schema) =>
                    schema
                    .required("Break In Time is required")
                    .test(
                        "is-after-breakOut-and-before-out",
                        "Break In must be after Break Out and before Out Time",
                        function (value) {
                        const { inTime, outTime, breakOut } = this.parent;
                        if (!inTime || !outTime || !breakOut || !value) return true;

                        const shift = normalizePair(inTime, outTime, "hh:mm A");
                        if (!shift) return true;

                        let breakOutMoment = moment(breakOut, "hh:mm A");
                        if (!breakOutMoment.isAfter(shift.inMoment)) breakOutMoment.add(1, "day");

                        let breakInMoment = moment(value, "hh:mm A");
                        if (!breakInMoment.isAfter(breakOutMoment)) breakInMoment.add(1, "day");

                        // breakIn must be after breakOut and before shift.out
                        return breakInMoment.isAfter(breakOutMoment) && breakInMoment.isBefore(shift.outMoment);
                        }
                    ),
                otherwise: (schema) => schema.nullable().notRequired(),
                }),
            }),
        onSubmit: async (values, { setSubmitting,resetForm }) => {
            try {
                const formatTo24 = (timeObj) => {
                    if (!timeObj) return "";
                    if (dayjs.isDayjs(timeObj)) return timeObj.format("HH:mm:ss");
                    const parsed = dayjs(timeObj, ["h:mm A", "hh:mm A"]);
                    return parsed.isValid() ? parsed.format("HH:mm:ss") : "";
                };

                // Build form data
                const formData = new FormData();
                formData.append("shift_code", values.shiftCode);
                formData.append("shift_name", values.shiftName);
                formData.append("shift_type", values.shiftType);
                formData.append("shift_in", formatTo24(values.inTime));
                formData.append("shift_out", formatTo24(values.outTime));
                formData.append("with_break", values.withBreak ? 1 : 0);
                formData.append(
                    "break_out",
                    values.withBreak ? formatTo24(values.breakOut) : ""
                );

                formData.append(
                    "break_in",
                    values.withBreak ? formatTo24(values.breakIn) : ""
                );
                formData.append("created_by", authDetails.user_id);
                formData.append("unit_id", authDetails.unit_id);

                // Add shift ID depending on mode
                formData.append("shift_id", isEdit ? shiftId : 0);

                // Dispatch create/update action
                dispatch(createShift(formData));
                resetForm()
                setIsEdit(false)
            } catch (error) {
                console.error("Shift save error:", error);
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Fetch data on component mount
    useEffect(() => {
        formik.resetForm();
        const authUserDetails = useAuthUserDetails();
        setAuthDetails(authUserDetails[0])
        const unitId = authUserDetails[0].unit_id
        dispatch(fetchShifts(unitId));
    }, [dispatch]);

    const formatTime = (timeStr) => {
        if (!timeStr) return "";

        // If already in 12-hour format (contains AM/PM), return as-is
        if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
            return timeStr.trim().toUpperCase();
        }

        // Expecting backend format like "14:30:00" or "09:15"
        const [hourStr, minuteStr] = timeStr.split(":");
        let hours = parseInt(hourStr, 10);
        const minutes = minuteStr ? minuteStr.padStart(2, "0") : "00";

        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;
    };

    const convertTo24Hour = (timeStr) => {
        if (!timeStr) return "";
        const [time, modifier] = timeStr.toUpperCase().split(/(AM|PM)/).filter(Boolean);
        let [hours, minutes] = time.split(":").map((t) => parseInt(t, 10));
        if (modifier === "PM" && hours < 12) hours += 12;
        if (modifier === "AM" && hours === 12) hours = 0;
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
    };



    const handleEdit = (row) => {
        console.log('row > ',row)
        setShiftId(row.shiftid)
        const parse24To12 = (timeStr) => {
            if (!timeStr) return "";
            const parsed = dayjs(timeStr, "HH:mm:ss");
            return parsed.isValid() ? parsed.format("hh:mm A") : "";
        };
        formik.setValues({
            shiftCode: row.shiftcode,
            shiftName: row.shiftName,
            // shiftType: row.shiftType,
            shiftType: row.shiftTypeid,
            inTime: parse24To12(row.shiftAPIIn),
            outTime: parse24To12(row.shiftAPIOut),
            breakOut: parse24To12(row.breakAPIOut),
            breakIn: parse24To12(row.breakAPIIn),
            withBreak: row.with_break,
        });
        setIsEdit(true);
    };


    // const columns = useMemo(
    //     () => [
    //         {
    //             Header: "Action",
    //             className: "serial-no",
    //             applyHeaderClass: true,
    //             Cell: (cellProps) => {
    //                 const isActive = cellProps.row.original.isActive;
    //                 return (
    //                     <div className="d-flex gap-3">
    //                         {/* <Link
    //                             to="#"
    //                             className="text-danger"
    //                             onClick={() =>
    //                                 confirmDelete({
    //                                     shiftid: cellProps.row.original.shiftid,
    //                                     createdby: cellProps.row.original.createdby,
    //                                 })}
    //                         >
    //                             <i
    //                                 className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
    //                                 id={`status-tooltip-${cellProps.row.id}`}
    //                             />
    //                             <UncontrolledTooltip placement="top" target={`status-tooltip-${cellProps.row.id}`}>
    //                                 {isActive ? "Mark Inactive" : "Mark Active"}
    //                             </UncontrolledTooltip>
    //                         </Link> */}
    //                         <Link
    //                             to="#"
    //                             className="text-success"
    //                             onClick={() => handleEdit(cellProps.row.original)}
    //                         >
    //                             <i className="bx bxs-edit font-size-18" id="edittooltip" />
    //                             <UncontrolledTooltip placement="top" target="edittooltip">
    //                                 Edit
    //                             </UncontrolledTooltip>
    //                         </Link>
    //                     </div>
    //                 );
    //             },
    //         },
    //         {
    //             Header: "S.No",
    //             Cell: ({ row }) => shifts.length - row.index,
    //             className: "serial-no",
    //             applyHeaderClass: true,
    //         },
    //         {
    //             Header: "Shift Code",
    //             accessor: "shiftcode",
    //         },
    //         {
    //             Header: "Shift Name",
    //             accessor: "shiftName",
    //         },
    //         {
    //             Header: "Shift Type",
    //             accessor: "shiftType",
    //         },
    //         {
    //             Header: "In Time",
    //             accessor: "shiftIn",
    //         },
    //         {
    //             Header: "Out Time",
    //             accessor: "shiftOut",
    //         },
    //         {
    //             Header: "Break Out",
    //             accessor: "breakOut",
    //         },
    //         {
    //             Header: "Break In",
    //             accessor: "breakIn",
    //         },
    //         {
    //             Header: "Created On",
    //             accessor: "createddate",
    //         },
    //         {
    //             Header: "Created By",
    //             accessor: "createdby",
    //         },
    //         //   {
    //         //     Header: "Created On",
    //         //     accessor: "createdOn",
    //         //     Cell: ({ value }) => {
    //         //       const date = new Date(value);
    //         //       const formattedDate = `${String(date.getDate()).padStart(2, "0")}-${String(
    //         //         date.getMonth() + 1
    //         //       ).padStart(2, "0")}-${date.getFullYear()}`;
    //         //       return formattedDate;
    //         //     },
    //         //   },
            
    //     ],
    //     [shifts]
    // );

    const columns = useMemo(() => {
        // Action column only if edit access is allowed
        const actionColumn =
            canEdit
            ? {
                Header: "Action",
                className: "serial-no",
                applyHeaderClass: true,
                Cell: (cellProps) => {
                    const uniqueId = cellProps.row.original.shiftid;

                    return (
                    <div className="d-flex gap-3">
                        {/* Edit Only */}
                        <Link
                        to="#"
                        className="text-success"
                        onClick={() => handleEdit(cellProps.row.original)}
                        >
                        <i
                            className="bx bxs-edit font-size-18"
                            id={`edittooltip-${uniqueId}`}
                        />
                        <UncontrolledTooltip
                            placement="top"
                            target={`edittooltip-${uniqueId}`}
                        >
                            Edit
                        </UncontrolledTooltip>
                        </Link>
                    </div>
                    );
                },
                }
            : null;

        return [
            ...(actionColumn ? [actionColumn] : []), // Only show if permission exists
            {
            Header: "S.No",
            Cell: ({ row }) => shifts.length - row.index,
            className: "serial-no",
            applyHeaderClass: true,
            },
            { Header: "Shift Code", accessor: "shiftcode" },
            { Header: "Shift Name", accessor: "shiftName" },
            { Header: "Shift Type", accessor: "shiftType" },
            { Header: "In Time", accessor: "shiftIn" },
            { Header: "Out Time", accessor: "shiftOut" },
            { Header: "Break Out", accessor: "breakOut" },
            { Header: "Break In", accessor: "breakIn" },
            { Header: "Created On", accessor: "createddate" },
            { Header: "Created By", accessor: "createdby" },
        ];
        }, [shifts, canEdit]);


    return (
        <React.Fragment>
            <div className="page-content">
                {/* <Loader loading={loading} /> */}
                <Container fluid>
                    <Breadcrumbs title="Shift" breadcrumbItem="Shift" />
                    <Row>
                        <Col lg="12">
                        {(canCreate || canEdit) && (
                            <Card>
                                <CardBody>
                                    <Form onSubmit={formik.handleSubmit}>
                                        <Row>
                                            {/* Shift Code */}
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="shiftCode">
                                                        Shift Code <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        id="shiftCode"
                                                        name="shiftCode"
                                                        type="text"
                                                        maxLength={10}
                                                        value={formik.values.shiftCode}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        invalid={formik.touched.shiftCode && !!formik.errors.shiftCode}
                                                    />
                                                    {formik.touched.shiftCode && formik.errors.shiftCode && (
                                                        <div className="text-danger">{formik.errors.shiftCode}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            {/* Shift Name */}
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="shiftName">
                                                        Shift Name <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        id="shiftName"
                                                        name="shiftName"
                                                        type="text"
                                                        maxLength={50}
                                                        value={formik.values.shiftName}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        invalid={formik.touched.shiftName && !!formik.errors.shiftName}
                                                    />
                                                     {formik.touched.shiftName && formik.errors.shiftName && (
                                                        <div className="text-danger">{formik.errors.shiftName}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                            {/* Shift Type */}
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="shiftType">
                                                        Shift Type <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        id="shiftType"
                                                        name="shiftType"
                                                        type="select"
                                                        value={formik.values.shiftType}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        invalid={formik.touched.shiftType && !!formik.errors.shiftType}
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="1">Day</option>
                                                        <option value="2">Night</option>
                                                    </Input>
                                                    {formik.touched.shiftType && formik.errors.shiftType && (
                                                        <div className="text-danger">{formik.errors.shiftType}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>

                                           {/* In Time */}
                                                <Col md={3}>
                                                <FormGroup>
                                                    <Label for="inTime">
                                                    In Time <span className="text-danger">*</span>
                                                    </Label>
                                                    <CustomTimePicker
                                                    name="inTime"
                                                    value={formik.values.inTime}
                                                    onChange={(val) => formik.setFieldValue("inTime", val)}
                                                    onBlur={() => formik.setFieldTouched("inTime", true)}
                                                    placeholder="Select Time"
                                                    format="hh:mm A"
                                                    />
                                                    {formik.touched.inTime && formik.errors.inTime && (
                                                    <div className="text-danger">{formik.errors.inTime}</div>
                                                    )}
                                                </FormGroup>
                                                </Col>

                                                {/* Out Time */}
                                                <Col md={3}>
                                                <FormGroup>
                                                    <Label for="outTime">
                                                    Out Time <span className="text-danger">*</span>
                                                    </Label>
                                                    <CustomTimePicker
                                                    name="outTime"
                                                    value={formik.values.outTime}
                                                    onChange={(val) => formik.setFieldValue("outTime", val)}
                                                    onBlur={() => formik.setFieldTouched("outTime", true)}
                                                    placeholder="Select Time"
                                                    format="hh:mm A"
                                                    />
                                                    {formik.touched.outTime && formik.errors.outTime && (
                                                    <div className="text-danger">{formik.errors.outTime}</div>
                                                    )}
                                                </FormGroup>
                                                </Col>
                                                {/* With Break */}
                                                <Col md={3} className="d-flex align-items-center">
                                                    <FormGroup check className="d-flex align-items-center gap-3">
                                                        <Label check className="gap-2">
                                                            <Input
                                                                type="checkbox"
                                                                id="withBreak"
                                                                name="withBreak"
                                                                checked={formik.values.withBreak}
                                                                onChange={formik.handleChange}
                                                            />
                                                            With Break
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                                {formik.values.withBreak && (
                                                <>
                                                <Col md={3}>
                                                <FormGroup>
                                                    <Label for="breakOut">
                                                    Break Out <span className="text-danger">*</span>
                                                    </Label>
                                                    <CustomTimePicker
                                                    name="breakOut"
                                                    value={formik.values.breakOut}
                                                    onChange={(val) => formik.setFieldValue("breakOut", val)}
                                                    onBlur={() => formik.setFieldTouched("breakOut", true)}
                                                    placeholder="Select Time"
                                                    format="hh:mm A"
                                                    />
                                                    {formik.touched.breakOut && formik.errors.breakOut && (
                                                    <div className="text-danger">{formik.errors.breakOut}</div>
                                                    )}
                                                </FormGroup>
                                                </Col>

                                                <Col md={3}>
                                                <FormGroup>
                                                    <Label for="breakIn">
                                                    Break In <span className="text-danger">*</span>
                                                    </Label>
                                                    <CustomTimePicker
                                                    name="breakIn"
                                                    value={formik.values.breakIn}
                                                    onChange={(val) => formik.setFieldValue("breakIn", val)}
                                                    onBlur={() => formik.setFieldTouched("breakIn", true)}
                                                    placeholder="Select Time"
                                                    format="hh:mm A"
                                                    />
                                                    {formik.touched.breakIn && formik.errors.breakIn && (
                                                    <div className="text-danger">{formik.errors.breakIn}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                                </>

                                            )}

                                            <div className="text-end mt-3">
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    className="mb-2 mb-md-0 btn button-save me-2"
                                                    disabled={formik.isSubmitting} > 
                                                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                                                
                                                {/* {formik.isSubmitting ? (
                                                        <>
                                                            <Spinner size="sm" /> Processing...
                                                        </>
                                                    ) : (
                                                        isEdit ? "Update" : "Save"
                                                    )} */}
                                            </Button>
                                            <Button type="reset" color="secondary" onClick={() => {
                                                formik.resetForm();
                                                setIsEdit(false);
                                            }}
                                                className="btn button-cancel me-2">
                                                <i className="fas fa-sync me-1" /> Cancel
                                            </Button>

                                        </div>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                        )}

                        <Card>
                            <CardBody>
                                <TableContainer
                                    columns={columns}
                                    data={shifts && shifts.length > 0 ? shifts : []}
                                    isGlobalFilter={true}
                                    customPageSize={10}
                                    className="custom-header-css"
                                />
                            </CardBody>
                        </Card>

                    </Col>
                </Row>
                <Modal isOpen={modal} toggle={toggle}>
                    <ModalBody>Are you sure you want to delete this shift?</ModalBody>
                    <ModalFooter>
                        <Button color="danger" onClick={handleDelete}>
                            Yes, Delete
                        </Button>{" "}
                        <Button color="secondary" onClick={toggle}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
       </React.Fragment >
    );
};

export default Shift;
